import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './ShippingPage.module.css';
import ShippingQueue from '../components/shipping/ShippingQueue';
import CreateShipmentForm from '../components/shipping/CreateShipmentForm';
import ShipArtworkModal from '../components/ShipArtworkModal';

import { getShippingLabels, getShippingRates, createShippingLabel } from '../services/shippingService';
import { api } from '../apiConfig';

const ShippingPage = () => {
  const [shippingQueue, setShippingQueue] = useState([]);
  const [purchasedLabels, setPurchasedLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [labelsLoading, setLabelsLoading] = useState(true);
  const [customShipmentData, setCustomShipmentData] = useState(null);
  const [customRates, setCustomRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [showFasterOptions, setShowFasterOptions] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [extraInsurance, setExtraInsurance] = useState(0);
  const [shippingDate, setShippingDate] = useState('today');
  const [ratesError, setRatesError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchShippingQueue();
    fetchPurchasedLabels();
    
    // Check if user is returning from successful purchase
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('purchased') === 'true') {
      const labelRequestId = urlParams.get('label_request_id');
      toast.success('🎉 Shipping label purchased successfully! Your label will appear below once processed.');
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Refresh the data after a short delay to allow webhook processing
      setTimeout(() => {
        fetchShippingQueue();
        fetchPurchasedLabels();
      }, 3000);
    }
    
    // Resume watching a checkout started earlier (same-tab fallback or a page reload)
    const checkPendingOrder = () => {
      const pendingOrder = localStorage.getItem('pending_shipping_order');
      if (pendingOrder) {
        try {
          const orderData = JSON.parse(pendingOrder);
          const timeSinceOrder = Date.now() - orderData.timestamp;
          
          if (orderData.draft_order_id && timeSinceOrder < 24 * 60 * 60 * 1000) {
            checkOrderStatus(orderData.draft_order_id);
          } else {
            localStorage.removeItem('pending_shipping_order');
          }
        } catch (error) {
          console.error('Error parsing pending order:', error);
          localStorage.removeItem('pending_shipping_order');
        }
      }
    };
    
    checkPendingOrder();
  }, []);

  const fetchShippingQueue = async () => {
    try {
      // Fetch unfulfilled orders that need shipping labels
      const apiUrl = import.meta.env.PROD 
        ? 'https://art-emerge-4744deb5e11a.herokuapp.com/api'
        : 'http://localhost:5051/api';
        
      const response = await fetch(`${apiUrl}/shipping/queue`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const queue = await response.json();
        setShippingQueue(queue);
      } else {
        setShippingQueue([]);
      }
    } catch (error) {
      toast.error('Failed to fetch shipping queue');
      setShippingQueue([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPurchasedLabels = async () => {
    try {
      const labels = await getShippingLabels();
      setPurchasedLabels(labels);
    } catch (error) {
      toast.error('Failed to fetch purchased labels');
      setPurchasedLabels([]);
    } finally {
      setLabelsLoading(false);
    }
  };

  const handleGetRatesForCustomShipment = async (shipmentData) => {
    try {
      setRatesLoading(true);
      setCustomRates([]);
      setSelectedRate(null);
      setShowFasterOptions(false);
      setRatesError(null);
      setCustomShipmentData(shipmentData);

      // Call the rates API directly
      const ratesData = await getShippingRates({
        recipient_name: shipmentData.recipient_name,
        recipient_phone: shipmentData.recipient_phone,
        recipient_address_line1: shipmentData.recipient_address_line1,
        recipient_address_line2: shipmentData.recipient_address_line2,
        recipient_city: shipmentData.recipient_city,
        recipient_state: shipmentData.recipient_state,
        recipient_postal_code: shipmentData.recipient_postal_code,
        recipient_country: shipmentData.recipient_country || 'US',
        ship_from_name: shipmentData.ship_from_name,
        ship_from_phone: shipmentData.ship_from_phone,
        ship_from_address_line1: shipmentData.ship_from_address_line1,
        ship_from_address_line2: shipmentData.ship_from_address_line2,
        ship_from_city: shipmentData.ship_from_city,
        ship_from_state: shipmentData.ship_from_state,
        ship_from_postal_code: shipmentData.ship_from_postal_code,
        ship_from_country: shipmentData.ship_from_country || 'US',
        weight: shipmentData.weight,
        weight_unit: shipmentData.weight_unit,
        width: shipmentData.width,
        height: shipmentData.height,
        depth: shipmentData.depth,
        dimension_unit: shipmentData.dimension_unit,
        packaging_type: shipmentData.packaging_type,
        insurance: extraInsurance // Default insurance
      });

      if (ratesData.rates && ratesData.rates.length > 0) {
        setCustomRates(ratesData.rates);
        
        // Scroll to rates section
        setTimeout(() => {
          const ratesSection = document.getElementById('custom-rates-section');
          if (ratesSection) {
            ratesSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
        return { success: true, rates: ratesData.rates };
      }

      throw new Error('No shipping rates available for this destination. Check the address, ZIP, and package size, then try again.');
    } catch (error) {
      const message = error.message || 'Failed to get shipping rates';
      setRatesError(message);
      toast.error(message, { autoClose: 10000 });
      console.error('Error getting rates:', error);
      setTimeout(() => {
        document.getElementById('rates-error-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      throw error; // Keep form data so the user can fix and retry
    } finally {
      setRatesLoading(false);
    }
  };

  const handlePurchaseCustomLabel = async () => {
    if (!selectedRate || !customShipmentData) {
      toast.error('Please select a shipping rate');
      return;
    }

    // ✅ DUPLICATE PREVENTION: Check if already in progress
    if (purchaseLoading) {
      toast.warning('Order creation already in progress, please wait...');
      return;
    }

    try {
      setPurchaseLoading(true);
      
      // Calculate total cost including insurance
      // Cap SEKO LTL insurance at $1000
      const isSeko = selectedRate?.carrier?.toLowerCase().includes('seko');
      const effectiveInsurance = isSeko ? Math.min(extraInsurance, 1000) : extraInsurance;
      const insuranceCost = effectiveInsurance * 0.01; // $0.01 per $1 of coverage
      const totalCost = selectedRate.price + insuranceCost;
      
      // Create a Shopify draft order for payment
      const draftOrderData = {
        ...customShipmentData,
        rate_id: selectedRate.id,
        insurance: effectiveInsurance, // Respect carrier limits (SEKO ≤ $1000)
        shipping_cost: selectedRate.price,
        insurance_cost: insuranceCost,
        total_cost: totalCost,
        carrier: selectedRate.carrier,
        service: selectedRate.service,
        est_days: selectedRate.est_days,
        shipping_date: shippingDate
      };

      // Debug: Check authentication
      const token = localStorage.getItem('token');
      console.log('🔑 Token exists:', !!token);
      console.log('🔑 Token preview:', token ? `${token.substring(0, 20)}...` : 'No token');
      
      // Check if user is logged in with Firebase
      const { auth } = await import('../firebaseConfig');
      const currentUser = auth.currentUser;
      console.log('👤 Firebase user:', currentUser ? currentUser.email : 'Not logged in');
      
      if (!currentUser) {
        toast.error('Please log in to purchase shipping labels');
        setPurchaseLoading(false);
        return;
      }
      
      // Get fresh token from Firebase
      const freshToken = await currentUser.getIdToken();
      console.log('🔑 Fresh token obtained:', !!freshToken);
      
      console.log('📦 Draft order data:', draftOrderData);

      // Call backend to create Shopify draft order
      const apiUrl = import.meta.env.PROD 
        ? 'https://art-emerge-4744deb5e11a.herokuapp.com/api'
        : 'http://localhost:5051/api';
      
      const response = await fetch(`${apiUrl}/shipping/create-payment-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${freshToken}` // Use fresh token instead of localStorage token
        },
        body: JSON.stringify(draftOrderData)
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', response.headers);

      // ✅ HANDLE DUPLICATE PREVENTION RESPONSE
      if (response.status === 409) {
        const errorData = await response.json();
        console.warn('🔒 Duplicate order prevented:', errorData);
        toast.warning('A similar order is already pending. Please complete or cancel the existing order first.');
        setPurchaseLoading(false);
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        console.error('❌ Full response:', response);
        
        // Try to parse error as JSON for better debugging
        try {
          const errorJson = JSON.parse(errorText);
          console.error('❌ Parsed error:', errorJson);
          throw new Error(`Failed to create payment order: ${response.status} - ${errorJson.details || errorJson.error}`);
        } catch (parseError) {
          throw new Error(`Failed to create payment order: ${response.status} - ${errorText}`);
        }
      }

      const { checkout_url, draft_order_id } = await response.json();
      
      // Store the draft order ID for later completion
      localStorage.setItem('pending_shipping_order', JSON.stringify({
        draft_order_id,
        shipment_data: draftOrderData,
        timestamp: Date.now()
      }));
      
      // Shopify's thank-you page can't send buyers back here, so checkout opens in a
      // new tab and this page watches the order until the label exists.
      const checkoutTab = window.open(checkout_url, '_blank');
      if (!checkoutTab) {
        window.location.href = checkout_url;
        return;
      }
      checkoutTab.opener = null;
      setPurchaseLoading(false);
      toast.info('Complete payment in the new tab. Your label will appear here once it is paid.', { autoClose: 8000 });
      checkOrderStatus(draft_order_id);
      
    } catch (error) {
      toast.error('Failed to create payment order');
      console.error('Error creating payment order:', error);
      setPurchaseLoading(false);
    }
  };

  // Polls every 5s for up to 30 minutes: covers the buyer finishing checkout plus label creation.
  const checkOrderStatus = async (draftOrderId, attempt = 0, announcedPaid = false) => {
    const retry = (paid) => {
      if (attempt < 360) setTimeout(() => checkOrderStatus(draftOrderId, attempt + 1, paid), 5000);
    };
    try {
      const { auth } = await import('../firebaseConfig');
      const user = auth.currentUser;
      if (!user) {
        retry(announcedPaid);
        return;
      }
      const apiUrl = import.meta.env.PROD 
        ? 'https://art-emerge-4744deb5e11a.herokuapp.com/api'
        : 'http://localhost:5051/api';
      const token = await user.getIdToken();
        
      const response = await fetch(`${apiUrl}/shipping/check-order-status/${draftOrderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status === 404) {
        localStorage.removeItem('pending_shipping_order');
        return;
      }
      if (!response.ok) {
        retry(announcedPaid);
        return;
      }

      const { status, tracking_number } = await response.json();
      if (status === 'label_created') {
        toast.success(`Payment received. Your label is ready (tracking ${tracking_number}) and has been emailed to you.`);
        localStorage.removeItem('pending_shipping_order');
        fetchPurchasedLabels();
        return;
      }
      if (status === 'paid') {
        if (!announcedPaid) toast.info('Payment received. Creating your shipping label...');
        // Label creation normally takes seconds; after ~2 minutes it failed and the team was alerted.
        if (announcedPaid && attempt > 0 && attempt % 24 === 0) {
          toast.warning('Your payment went through, but the label is taking longer than usual. We have been notified and will email it to you.', { autoClose: 10000 });
        }
        retry(true);
        return;
      }
      retry(announcedPaid);
    } catch (error) {
      console.error('Error checking order status:', error);
      retry(announcedPaid);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Shipping</h1>
      
      <div className={styles.content}>
        
        {/* Section 1: Orders needing fulfillment */}
        <section className={styles.queueSection}>
          <h2 className={styles.sectionTitle}>Shipping Queue</h2>
          <p className={styles.sectionDescription}>Orders from your sales that need shipping labels created</p>
          <ShippingQueue 
            labels={shippingQueue}
            isLoading={isLoading}
            onRefresh={fetchShippingQueue}
          />
        </section>

        {/* Section 2: Previously purchased labels */}
        <section className={styles.labelsSection}>
          <h2 className={styles.sectionTitle}>Purchased Labels</h2>
          <p className={styles.sectionDescription}>Download and track your shipping labels</p>
          
          {labelsLoading ? (
            <div className={styles.emptyState}>
              <p>Loading labels...</p>
            </div>
          ) : purchasedLabels.length > 0 ? (
            <div className={styles.labelsGrid}>
              {purchasedLabels.map(label => (
                <div key={label.id} className={styles.labelCard}>
                  <div className={styles.labelHeader}>
                    <h3 className={styles.labelTitle}>
                      {label.shopify_order_id || 'Custom Shipment'}
                    </h3>
                    <span className={styles.labelStatus}>
                      {label.status === 'purchased' ? 'Ready' : 'Pending'}
                    </span>
                  </div>
                  
                  <div className={styles.labelDetails}>
                    <div className={styles.labelInfo}>
                      <div className={styles.labelRecipient}>
                        <strong>To:</strong> {label.recipient_name}
                      </div>
                      <div className={styles.labelAddress}>
                        {label.recipient_city}, {label.recipient_state} {label.recipient_postal_code}
                      </div>
                      <div className={styles.labelShipping}>
                        <strong>Carrier:</strong> {label.carrier || 'Unknown'} - {label.service || 'Standard'}
                      </div>
                      {label.tracking_number && (
                        <div className={styles.labelTracking}>
                          <strong>Tracking:</strong> {label.tracking_number}
                        </div>
                      )}
                      <div className={styles.labelCost}>
                        <strong>Cost:</strong> ${label.cost || '0.00'}
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.labelActions}>
                    <button 
                      className={styles.downloadButton}
                      onClick={async () => {
                        try {
                          // First try to get the label data to check if it's a data URL
                          const apiUrl = import.meta.env.PROD 
                            ? 'https://art-emerge-4744deb5e11a.herokuapp.com'
                            : 'http://localhost:5051';
                          
                          // Get the label data first
                          const labelResponse = await fetch(`${apiUrl}/api/shipping/labels`, {
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                          });
                          
                          if (!labelResponse.ok) {
                            throw new Error('Failed to get label data');
                          }
                          
                          const labels = await labelResponse.json();
                          const currentLabel = labels.find(l => l.id === label.id);
                          
                          if (!currentLabel || !currentLabel.label_url) {
                            throw new Error('No label URL available');
                          }
                          
                          // Handle data URLs (text labels) directly
                          if (currentLabel.label_url.startsWith('data:')) {
                            const a = document.createElement('a');
                            a.href = currentLabel.label_url;
                            
                            // Determine file extension based on data URL type
                            const fileExtension = currentLabel.label_url.includes('text/plain') ? 'txt' : 'pdf';
                            a.download = `shipping-label-${label.tracking_number}.${fileExtension}`;
                            
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            toast.success('Shipping label downloaded!');
                            return;
                          }
                          
                          // Handle regular URLs (PDF blobs) - fallback to old behavior
                          const downloadUrl = `${apiUrl}/api/shipping/labels/${label.id}/download`;
                          const response = await fetch(downloadUrl, {
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                          });
                          
                          if (!response.ok) {
                            throw new Error('Download failed');
                          }
                          
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `shipping-label-${label.tracking_number}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                          toast.success('Shipping label downloaded!');
                          
                        } catch (error) {
                          console.error('Download failed:', error);
                          toast.error('Failed to download shipping label');
                        }
                      }}
                    >
                      📄 Download Label
                    </button>
                    {label.tracking_number && (
                      <button 
                        onClick={() => {
                          // Copy tracking number to clipboard
                          navigator.clipboard.writeText(label.tracking_number);
                          toast.success('Tracking number copied to clipboard!');
                        }}
                        className={styles.copyButton}
                      >
                        📋 Copy Tracking
                      </button>
                    )}
                  </div>
                  
                  <div className={styles.labelFooter}>
                    <small>Created: {new Date(label.created_at).toLocaleDateString()}</small>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No shipping labels yet</p>
              <small>Labels you purchase will appear here</small>
            </div>
          )}
        </section>
        
        {/* Section 3: Create custom shipment */}
        <section className={styles.createSection}>
          <h2 className={styles.sectionTitle}>Create a Custom Shipment</h2>
          <p className={styles.sectionDescription}>Create and purchase shipping labels for one-off shipments</p>
          <CreateShipmentForm onSubmit={handleGetRatesForCustomShipment} />

          {ratesError && !ratesLoading && (
            <div
              id="rates-error-section"
              role="alert"
              style={{
                marginTop: '1rem',
                padding: '12px 14px',
                borderRadius: 8,
                border: '1px solid #f59e0b',
                background: '#fffbeb',
                color: '#92400e'
              }}
            >
              <strong>Could not get shipping rates.</strong>
              <div style={{ marginTop: 6 }}>{ratesError}</div>
              <div style={{ marginTop: 8, fontSize: '0.95rem' }}>
                Your form was kept so you can correct the address or package details and try again.
              </div>
            </div>
          )}
          
          {/* Loading State */}
          {ratesLoading && (
            <div className={styles.loadingSection}>
              <div className={styles.spinner}></div>
              <p>Getting shipping rates...</p>
            </div>
          )}

          {/* Rates Display Section */}
          {customRates.length > 0 && (
            <div id="custom-rates-section" className={styles.ratesSection}>
              <h3 className={styles.ratesTitle}>Available Shipping Options</h3>
              <div className={styles.realRatesNotice}>
                <span className={styles.realRatesIcon}>✓</span>
                <span>Real-time rates from carriers (FedEx, UPS, USPS, DHL)</span>
              </div>
              
              {/* Insurance Selection - ParcelGuard */}
              <div className={styles.insuranceSection}>
                <h4 className={styles.insuranceTitle}>📦 ParcelGuard Protection</h4>
                <p className={styles.insuranceDescription}>Optional insurance for your shipment</p>
                <div className={styles.insuranceOptions}>
                  <label className={styles.insuranceOption} style={{ gap: '1rem', alignItems: 'center' }}>
                    <span>Coverage amount</span>
                    <select
                      value={extraInsurance}
                      onChange={(e) => setExtraInsurance(Number(e.target.value))}
                      style={{ minWidth: '240px' }}
                    >
                      {(() => {
                        const options = [];
                        options.push(<option key={0} value={0}>No additional coverage</option>);
                        for (let amt = 100; amt <= 10000; amt += 100) {
                          const cost = (amt * 0.01).toFixed(2);
                          options.push(
                            <option key={amt} value={amt}>${amt.toLocaleString()} (+${cost})</option>
                          );
                        }
                        return options;
                      })()}
                    </select>
                  </label>
                  <small style={{ color: '#555', display: 'block', marginTop: 8 }}>Insurance billed at $0.01 per $1 of coverage.</small>
                </div>
              </div>
              
              {/* Shipping Options */}
              <div className={styles.ratesGrid}>
                {customRates.some(r => (r.carrier || '').toLowerCase().includes('seko')) && (
                  <div style={{ gridColumn: '1 / -1', color: '#1f2937', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                    <strong>SEKO LTL Requirement:</strong> Shipments must be fully crated and on a pallet for pickup. The carrier will call after label creation to confirm details and schedule pickup.
                  </div>
                )}
                {customRates.filter(rate => rate.price > 0 && (showFasterOptions || rate.recommended)).map(rate => (
                  <div 
                    key={rate.id} 
                    className={`${styles.rateCard} ${selectedRate?.id === rate.id ? styles.selectedRate : ''}`}
                    onClick={() => setSelectedRate(rate)}
                  >
                    <input 
                      type="radio" 
                      name="customRate" 
                      value={rate.id} 
                      checked={selectedRate?.id === rate.id} 
                      onChange={() => setSelectedRate(rate)} 
                    />
                    <div className={styles.rateInfo}>
                      <div className={styles.carrierInfo}>
                        <strong>{rate.carrier || 'Carrier'} {rate.service || ''}</strong>
                        <span className={styles.estimatedDays}>Est. {rate.est_days || '?'} days</span>
                        {rate.metadata?.is_real_shopify_rate && (
                          <span className={styles.realRateBadge}>✓ Real-time Rate</span>
                        )}
                        {rate.metadata?.is_discounted && (
                          <span className={styles.discountedBadge}>Shopify Discounted Rate</span>
                        )}
                      </div>
                      <div className={styles.priceInfo}>
                        <div className={styles.price}>
                          {(() => {
                            const isSeko = (rate.carrier || '').toLowerCase().includes('seko');
                            const effectiveIns = isSeko ? Math.min(extraInsurance, 1000) : extraInsurance;
                            const insuranceCost = (effectiveIns * 0.01) || 0;
                            const total = (rate.price || 0) + insuranceCost;
                            return `$${total.toFixed(2)}`;
                          })()}
                        </div>
                        <div className={styles.insurance}>
                          {(() => {
                            const isSeko = (rate.carrier || '').toLowerCase().includes('seko');
                            const effectiveIns = isSeko ? Math.min(extraInsurance, 1000) : extraInsurance;
                            if (effectiveIns > 0) {
                              const surcharge = (effectiveIns * 0.01).toFixed(2);
                              const capNote = isSeko && extraInsurance > 1000 ? ' (capped at $1000 for SEKO)' : '';
                              return `Includes ParcelGuard $${effectiveIns} coverage (+$${surcharge})${capNote}`;
                            }
                            return 'No insurance selected';
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {customRates.some(rate => rate.price > 0 && !rate.recommended) && (
                <button
                  type="button"
                  className={styles.copyButton}
                  style={{ marginTop: 12 }}
                  onClick={() => {
                    if (showFasterOptions && selectedRate && !selectedRate.recommended) setSelectedRate(null);
                    setShowFasterOptions(show => !show);
                  }}
                >
                  {showFasterOptions ? 'Show fewer options' : 'Show faster options'}
                </button>
              )}
              
              {selectedRate && (
                <div className={styles.purchaseSection}>
                  {(selectedRate.carrier || '').toLowerCase().includes('seko') && (
                    <div style={{ color: '#1f2937', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6, padding: '10px 12px', marginBottom: 10 }}>
                      <strong>SEKO LTL Requirement:</strong> Your item must be crated and on a pallet before pickup.
                    </div>
                  )}
                  <button 
                    onClick={handlePurchaseCustomLabel} 
                    className={styles.purchaseButton}
                    disabled={purchaseLoading}
                  >
                    {purchaseLoading ? (
                      <>
                        <span className={styles.spinner}></span>
                        Creating Label...
                      </>
                    ) : 'Continue to Payment'}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {/* Keep modal only for queue orders that have pre-filled data */}
      {customShipmentData && customShipmentData.shopify_order_id && customShipmentData.shopify_order_id.startsWith('custom-') === false && (
        <ShipArtworkModal 
          shipment={customShipmentData} 
          onClose={() => setCustomShipmentData(null)}
          onSuccess={() => {
            toast.success('Shipping label created successfully');
            fetchPurchasedLabels();
            setCustomShipmentData(null);
          }}
        />
      )}
    </div>
  );
};

export default ShippingPage; 